import type { PlatformPlanListItem } from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Package, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPlatformPlansQueryOptions } from "@/api/platformPlans";
import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute("/_auth/app/_app/platform-plans/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		context.queryClient.ensureQueryData(
			fetchPlatformPlansQueryOptions(token, { includeInactive: true }),
		);
		return { userId, token };
	},
	component: PlatformPlansPage,
});

function formatPrice(cents: number, currency: string): string {
	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency,
	}).format(cents / 100);
}

function PlatformPlansPage() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();
	const [includeInactive, setIncludeInactive] = useState(true);

	const {
		data: plans,
		isLoading,
		error,
	} = useSuspenseQuery(
		fetchPlatformPlansQueryOptions(token, { includeInactive }),
	);

	const handleRowClick = useCallback(
		(plan: PlatformPlanListItem) => {
			navigate({
				to: "/app/platform-plans/$planId",
				params: { planId: plan.id },
			});
		},
		[navigate],
	);

	const columns: ColumnDef<PlatformPlanListItem>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: t("platformPlans.table.name"),
				cell: ({ row }) => (
					<div className="font-medium flex items-center gap-2">
						{row.original.name}
						{row.original.isDefault && (
							<span className="badge badge-primary badge-sm">
								{t("platformPlans.table.defaultBadge")}
							</span>
						)}
					</div>
				),
			},
			{
				accessorKey: "slug",
				header: t("platformPlans.table.slug"),
				cell: ({ row }) => (
					<span className="font-mono text-sm">{row.original.slug}</span>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "tier",
				header: t("platformPlans.table.tier"),
				cell: ({ row }) => row.original.tier,
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "monthlyPriceCents",
				header: t("platformPlans.table.monthly"),
				cell: ({ row }) => (
					<span className="font-mono text-sm tabular-nums">
						{formatPrice(row.original.monthlyPriceCents, row.original.currency)}
					</span>
				),
			},
			{
				accessorKey: "yearlyPriceCents",
				header: t("platformPlans.table.yearly"),
				cell: ({ row }) => (
					<span className="font-mono text-sm tabular-nums">
						{formatPrice(row.original.yearlyPriceCents, row.original.currency)}
					</span>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "isActive",
				header: t("platformPlans.table.status"),
				cell: ({ row }) => (
					<span
						className={`badge badge-sm ${
							row.original.isActive ? "badge-success" : "badge-ghost"
						}`}
					>
						{row.original.isActive
							? t("platformPlans.status.active")
							: t("platformPlans.status.archived")}
					</span>
				),
			},
			{
				accessorKey: "subscriptionsCount",
				header: t("platformPlans.table.subscriptions"),
				cell: ({ row }) => (
					<span className="font-mono text-sm tabular-nums">
						{row.original.subscriptionsCount}
					</span>
				),
				meta: { hiddenOnMobile: true },
			},
		],
		[t],
	);

	return (
		<div className="container mx-auto p-6 space-y-6">
			<div className="flex items-center justify-between flex-wrap gap-2">
				<h1 className="text-2xl font-bold text-base-content">
					{t("platformPlans.title")}
				</h1>
				<div className="flex items-center gap-3">
					<label className="flex items-center gap-2 text-sm cursor-pointer">
						<input
							type="checkbox"
							className="checkbox checkbox-sm"
							checked={includeInactive}
							onChange={(event) => setIncludeInactive(event.target.checked)}
						/>
						{t("platformPlans.list.showArchived")}
					</label>
					<Button
						onClick={() => navigate({ to: "/app/platform-plans/new" })}
						data-testid="platform-plans-create-button"
					>
						<Plus className="h-4 w-4 mr-1" />
						{t("platformPlans.list.create")}
					</Button>
				</div>
			</div>

			<div className="card bg-base-100 shadow-sm">
				<div className="card-body p-0">
					<DataTable
						data={plans}
						columns={columns}
						isLoading={isLoading}
						error={error}
						testId="platform-plans"
						emptyState={{
							title: t("platformPlans.empty.title"),
							description: t("platformPlans.empty.description"),
							icon: Package,
						}}
						onRowClick={handleRowClick}
					/>
				</div>
			</div>
		</div>
	);
}
